import React, { FC, useEffect, useState } from "react";
import Breadcrumbs from "@/components/Breadcrumbs";
import { usePageStore } from "@/hooks/usePageStore";
import { Box, CardContent, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "@/utils/theme";
import { yupResolver } from "@hookform/resolvers/yup";
import Card from "@mui/material/Card";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import LightTooltip from "@/components/LightTooltip";
import Tabs from "@mui/material/Tabs";
import TabPanel from "@/components/TabPanel";
import Tab from "@mui/material/Tab";
import { a11yProps } from "@/utils/a11yProps";
import { FormProvider, useForm } from "react-hook-form";
import { TabProgramGeneral } from "@/ui/content/management/programs/TabProgramGeneral";
import { TabProgramOUs } from "@/ui/content/management/programs/TabProgramOUs";
import { TabProgramConfiguration } from "@/ui/content/management/programs/TabProgramConfiguration";
import { TabProgramGeofence } from "@/ui/content/management/programs/TabProgramGeofence";
import createProgramSchema, {
  CreateProgramFormData,
} from "@/ui/content/management/programs/validation.schema";
import Button from "@/components/Button";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  CalculationMethodEnum,
  IncentiveStructureEnum,
  ProgramStatusEnum,
  ValidationRateEnum,
  YesNoEnum,
} from "@/domain/programs.enum";
import {
  useGetProgram,
  useUpdateProgram,
} from "@/hooks/management/usePrograms.manage";
import { ProgramPayload } from "@/domain/programs.interface";
import { ErrorResponse } from "@/domain/error.interface";
import { useNavigate, useParams } from "react-router-dom";
import { getDateOnly } from "@/utils/dateUtils";

dayjs.extend(utc);

const EditProgramPage: FC = () => {
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();
  const { t } = useTranslation("program");
  const { t: tCommon } = useTranslation("common");
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const { programId } = useParams();

  const createAuditLog = useCreateAuditlog();
  const updateProgram = useUpdateProgram();
  const navigate = useNavigate();

  const { data: program } = useGetProgram(programId || "");

  useEffect(() => {
    setPageTitle(t("title"));
  }, []);

  const [selectedTab, setSelectedTab] = useState(0);

  const methods = useForm<CreateProgramFormData>({
    resolver: yupResolver(createProgramSchema(tCommon)),
    mode: "all",
    defaultValues: {
      status: ProgramStatusEnum.ACTIVE,
      startDate: dayjs().format("YYYY-MM-DD"),
      orgUnitIds: [],
      cmRoles: [],
      additionalStaffRoles: [],
      weekdays: [],
      formFactors: [],
      events: [],
      assignCMCoordinator: "",
      enabledFormFactor2: false,
      minutesPingGeofenceValidationRequest: undefined,
      metersGeofenceAccuracyThreshold: undefined,
      metersRadiusGeofenceRequestValidation: undefined,
      requestGeofenceValidationsPerDay: undefined,
      minimumMinsGeofenceBetweenRequestValidations: undefined,
      minutesPingGeofenceAlive: undefined,
    },
  });

  useEffect(() => {
    if (program) {
      methods.reset({
        name: program?.name ?? "",
        status: program?.status ?? "",
        cmRoles: program?.roleId ?? [],
        additionalStaffRoles: program?.additionalStaffRoles ?? [],

        // program numbers
        maxParticipants: program?.maxParticipants ?? undefined,
        programDuration: program?.duration ?? undefined,
        programBudgetLimit: program?.budgetLimit ?? undefined,

        // parameters
        eligibilityConfirmation: program?.parameters?.eligibilityConfirmation
          ? YesNoEnum.YES
          : YesNoEnum.NO,
        consentForm: program?.parameters?.consentForm
          ? YesNoEnum.YES
          : YesNoEnum.NO,
        enableOnDemandIncentive: program?.parameters?.enableOnDemandIncentive
          ? YesNoEnum.YES
          : YesNoEnum.NO,
        minAmount: program?.parameters?.minAmount ?? undefined,
        maxAmount: program?.parameters?.maxAmount ?? undefined,
        maxEarnings: program?.parameters?.maxEarnings ?? undefined,
        incentiveStructure: program?.parameters?.incentiveStructure ?? "",
        startDate: program?.parameters?.startDate
          ? dayjs.utc(program.parameters.startDate).format("YYYY-MM-DD")
          : "",
        endDate: program?.parameters?.endDate
          ? dayjs.utc(program.parameters.endDate).format("YYYY-MM-DD")
          : "",
        programDays: program?.parameters?.programDays ?? undefined,

        orgUnitIds: program?.orgUnitIds ?? [],

        // incentive ladder
        calculationMethod:
          program?.configuration?.incentiveLadder?.calculationMethod ?? "",
        basedIncentiveNegativeResults:
          program?.configuration?.incentiveLadder
            ?.basedIncentiveNegativeResults ??
          program?.configuration?.incentiveLadder?.basedIncentiveNegative ??
          undefined,
        basedIncentivePositiveResults:
          program?.configuration?.incentiveLadder
            ?.basedIncentivePositiveResults ??
          program?.configuration?.incentiveLadder?.basedIncentivePositiveR ??
          undefined,
        incrementValueNegative:
          program?.configuration?.incentiveLadder?.incrementValueNegative ??
          undefined,
        maxIncentiveNegativeRe:
          program?.configuration?.incentiveLadder?.maxIncentiveNegativeRe ??
          undefined,
        resetAmount:
          program?.configuration?.incentiveLadder?.resetAmount ?? undefined,
        incentiveAccountCreation:
          program?.configuration?.incentiveLadder?.incentiveAccountCreation ??
          undefined,
        incentiveFirstValidation:
          program?.configuration?.incentiveLadder?.incentiveFirstValidation ??
          undefined,
        incentiveValidationRequest:
          program?.configuration?.incentiveLadder?.incentiveValidationRequest ??
          undefined,
        incentiveOnDemandValidation:
          program?.configuration?.incentiveLadder
            ?.incentiveOnDemandValidation ?? undefined,
        incentiveNonCarriage:
          program?.configuration?.incentiveLadder?.incentiveNonCarriage ??
          undefined,

        // validation limits
        timeToSubmitValidation:
          program?.configuration?.validationLimits?.timeSubmitValidation ??
          undefined,
        validationLimitReminder:
          program?.configuration?.validationLimits?.validationTimeReminder ??
          undefined,

        // validation rates
        validationRates: program?.configuration?.validationRates
          ?.validationRates as ValidationRateEnum,
        weekdays: program?.configuration?.validationRates?.weekdays ?? [],

        // contact
        fullName: program?.configuration?.contact?.name ?? "",
        email: program?.configuration?.contact?.email ?? "",
        phoneNumber: program?.configuration?.contact?.phoneNumber ?? "",

        // re-enrollment
        numberOfDays: program?.configuration?.reenrollmentDays ?? undefined,

        // form factors
        formFactors:
          program?.configuration?.formFactors?.map((f) => f.type) ?? [],
        assignCMCoordinator: program?.configuration?.assignCMCoordinator ?? "",
        enabledFormFactor2: program?.configuration?.enabledFormFactor2 ?? false,
        mobilePhoneNumber: program?.configuration?.mobilePhoneNumber ?? "",
        mobileDateOfBirth: program?.configuration?.mobileDateOfBirth ?? "",

        // validation request settings
        minutesValidationTimer:
          program?.configuration?.validationRequestSettings
            ?.minutesValidationTimer ?? undefined,
        timerValidationRequestOpened:
          program?.configuration?.validationRequestSettings
            ?.timerValidationRequestOpened ?? undefined,
        typeRequestValidation:
          program?.configuration?.validationRequestSettings
            ?.typeRequestValidation ?? "",
        distanceFilterGeofenceRequestValidation:
          program?.configuration?.validationRequestSettings
            ?.distanceFilterGeofenceRequestValidation ?? undefined,
        desiredAccuracy:
          program?.configuration?.validationRequestSettings?.desiredAccuracy ??
          "",
        showGeofenceChip:
          program?.configuration?.validationRequestSettings?.showGeofenceChip ??
          false,
        automaticGeofenceTrigger:
          program?.configuration?.validationRequestSettings
            ?.automaticGeofenceTrigger ?? false,
        minutesTimeToCompleteValidation:
          program?.configuration?.validationRequestSettings
            ?.minutesTimeToCompleteValidation ?? undefined,
        validationRequestMaxAttempts:
          program?.configuration?.validationRequestSettings
            ?.validationRequestMaxAttempts ?? undefined,
        enabledRewardFirstValidationSuccess:
          program?.configuration?.validationRequestSettings
            ?.enabledRewardFirstValidationSuccess ?? false,
        enabledRewardValidationRequestSuccess:
          program?.configuration?.validationRequestSettings
            ?.enabledRewardValidationRequestSuccess ?? false,
        enabledRewardOnDemandValidationSuccess:
          program?.configuration?.validationRequestSettings
            ?.enabledRewardOnDemandValidationSuccess ?? false,
        enabledRewardNonCarriage:
          program?.configuration?.validationRequestSettings
            ?.enabledRewardNonCarriage ?? false,
        onDemandValidationRequest:
          program?.configuration?.validationRequestSettings
            ?.onDemandValidationRequest ?? false,
        onDemandValidationPeriodDays:
          program?.configuration?.validationRequestSettings
            ?.onDemandValidationPeriodDays ?? undefined,
        onDemandValidationDays:
          program?.configuration?.validationRequestSettings
            ?.onDemandValidationDays ?? [],
        minutesPingGeofenceValidationRequest:
          program?.configuration?.validationRequestSettings
            ?.minutesPingGeofenceValidationRequest ?? undefined,
        metersGeofenceAccuracyThreshold:
          program?.configuration?.validationRequestSettings
            ?.metersGeofenceAccuracyThreshold ?? undefined,
        metersRadiusGeofenceRequestValidation:
          program?.configuration?.validationRequestSettings
            ?.metersRadiusGeofenceRequestValidation ?? undefined,
        requestGeofenceValidationsPerDay:
          program?.configuration?.validationRequestSettings
            ?.requestGeofenceValidationsPerDay ?? undefined,
        minimumMinsGeofenceBetweenRequestValidations:
          program?.configuration?.validationRequestSettings
            ?.minimumMinsGeofenceBetweenRequestValidations ?? undefined,
        minutesPingGeofenceAlive:
          program?.configuration?.validationRequestSettings
            ?.minutesPingGeofenceAlive ?? undefined,
        stickyAndroidNotificationTitle:
          program?.configuration?.validationRequestSettings
            ?.stickyAndroidNotificationTitle ?? "",
        stickyAndroidNotificationText:
          program?.configuration?.validationRequestSettings
            ?.stickyAndroidNotificationText ?? "",

        // configuration
        enabledRewardUponAccountCreation:
          program?.configuration?.enabledRewardUponAccountCreation ?? false,

        // events
        events: program?.events ?? [],
        // configuration
        participantUserGuideURL:
          program?.configuration?.participantUserGuideURL ?? [],

        educationalMaterials:
          program?.configuration?.educationalMaterials ?? [],
      });
    }
  }, [program, methods]);

  const handleTabChange = async (
    event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setSelectedTab(newValue);
  };

  const onSubmit = async (data: CreateProgramFormData) => {
    // Map form factor keys to display names
    const formFactorNameMap: Record<string, string> = {
      NYXOID_NASAL_SPRAY: "Nyxoid Nasal Spray",
      PEBBLE_NASAL_SPRAY: "Pebble Nasal Spray",
      PRENOXAD_INJECTABLE: "Prenoxad Injectable",
    };

    const payload: ProgramPayload = {
      // general information
      name: data.name as string,
      status: data.status as ProgramStatusEnum,
      roleId: data.cmRoles as string[],
      additionalStaffRoles: data.additionalStaffRoles as string[],

      // default values
      programType: "individual",
      updatedBy: userInfo?._id,

      // program numbers
      maxParticipants: data.maxParticipants
        ? Number(data.maxParticipants)
        : undefined,
      duration: data.programDuration ? Number(data.programDuration) : undefined,
      budgetLimit: data.programBudgetLimit
        ? Number(data.programBudgetLimit)
        : undefined,

      parameters: {
        eligibilityConfirmation: data.eligibilityConfirmation === YesNoEnum.YES,
        consentForm: data.consentForm === YesNoEnum.YES,
        enableOnDemandIncentive: data.enableOnDemandIncentive === YesNoEnum.YES,
        minAmount: data.minAmount ? Number(data.minAmount) : undefined,
        maxAmount: data.maxAmount ? Number(data.maxAmount) : undefined,
        maxEarnings: data.maxEarnings ? Number(data.maxEarnings) : undefined,
        incentiveStructure: data.incentiveStructure as IncentiveStructureEnum,
        startDate: getDateOnly(data.startDate as dayjs.Dayjs | null) || "",
        endDate: data.endDate
          ? getDateOnly(data.endDate as dayjs.Dayjs | null) || ""
          : undefined,
        programDays: data.programDays ? Number(data.programDays) : undefined,
      },

      // program ous
      orgUnitIds: data.orgUnitIds as string[],

      configuration: {
        incentiveLadder: {
          calculationMethod: data.calculationMethod as CalculationMethodEnum,
          basedIncentiveNegativeResults: Number(
            data.basedIncentiveNegativeResults,
          ),
          basedIncentivePositiveResults: Number(
            data.basedIncentivePositiveResults,
          ),
          incentiveAccountCreation:
            data.incentiveAccountCreation !== undefined &&
            data.incentiveAccountCreation !== null &&
            data.incentiveAccountCreation !== ""
              ? Number(data.incentiveAccountCreation)
              : undefined,
          incentiveFirstValidation:
            data.incentiveFirstValidation !== undefined &&
            data.incentiveFirstValidation !== null &&
            data.incentiveFirstValidation !== ""
              ? Number(data.incentiveFirstValidation)
              : undefined,
          incentiveValidationRequest:
            data.incentiveValidationRequest !== undefined &&
            data.incentiveValidationRequest !== null &&
            data.incentiveValidationRequest !== ""
              ? Number(data.incentiveValidationRequest)
              : undefined,
          incentiveOnDemandValidation:
            data.incentiveOnDemandValidation !== undefined &&
            data.incentiveOnDemandValidation !== null &&
            data.incentiveOnDemandValidation !== ""
              ? Number(data.incentiveOnDemandValidation)
              : undefined,
          incentiveNonCarriage:
            data.incentiveNonCarriage !== undefined &&
            data.incentiveNonCarriage !== null &&
            data.incentiveNonCarriage !== ""
              ? Number(data.incentiveNonCarriage)
              : undefined,
          incrementValueNegative: data.incrementValueNegative
            ? Number(data.incrementValueNegative)
            : undefined,
          maxIncentiveNegativeRe: data.maxIncentiveNegativeRe
            ? Number(data.maxIncentiveNegativeRe)
            : undefined,
          resetAmount: data.resetAmount ? Number(data.resetAmount) : undefined,
        },
        validationLimits: {
          timeSubmitValidation: Number(data.timeToSubmitValidation),
          validationTimeReminder: data.validationLimitReminder
            ? Number(data.validationLimitReminder)
            : undefined,
        },
        validationRates: {
          needsValidation: true,
          validationRates: data.validationRates as ValidationRateEnum,
          weekdays: data.weekdays as string[],
        },
        contact: {
          name: data.fullName ? (data.fullName as string) : undefined,
          email: data.email ? (data.email as string) : undefined,
          phoneNumber: data.phoneNumber
            ? (data.phoneNumber as string)
            : undefined,
        },
        reenrollmentDays: data.numberOfDays
          ? Number(data.numberOfDays)
          : undefined,
        formFactors:
          Array.isArray(data.formFactors) && data.formFactors.length > 0
            ? (data.formFactors as string[]).map((factor) => ({
                type: factor,
                name: formFactorNameMap[factor] || factor,
              }))
            : undefined,
        assignCMCoordinator: (data.assignCMCoordinator as any) || undefined,
        enabledFormFactor2: (data.enabledFormFactor2 as boolean) ?? false,
        mobilePhoneNumber: (data.mobilePhoneNumber as any) || undefined,
        mobileDateOfBirth: (data.mobileDateOfBirth as any) || undefined,
        validationRequestSettings: {
          minutesValidationTimer: data.minutesValidationTimer
            ? Number(data.minutesValidationTimer)
            : undefined,
          timerValidationRequestOpened: data.timerValidationRequestOpened
            ? Number(data.timerValidationRequestOpened)
            : undefined,
          typeRequestValidation:
            (data.typeRequestValidation as string) || undefined,
          distanceFilterGeofenceRequestValidation:
            data.distanceFilterGeofenceRequestValidation
              ? Number(data.distanceFilterGeofenceRequestValidation)
              : undefined,
          desiredAccuracy: (data.desiredAccuracy as string) || undefined,
          showGeofenceChip: (data.showGeofenceChip as boolean) ?? false,
          automaticGeofenceTrigger:
            (data.automaticGeofenceTrigger as boolean) ?? false,
          minutesTimeToCompleteValidation: data.minutesTimeToCompleteValidation
            ? Number(data.minutesTimeToCompleteValidation)
            : undefined,
          validationRequestMaxAttempts: data.validationRequestMaxAttempts
            ? Number(data.validationRequestMaxAttempts)
            : undefined,
          enabledRewardFirstValidationSuccess:
            (data.enabledRewardFirstValidationSuccess as boolean) ?? false,
          enabledRewardValidationRequestSuccess:
            (data.enabledRewardValidationRequestSuccess as boolean) ?? false,
          enabledRewardOnDemandValidationSuccess:
            (data.enabledRewardOnDemandValidationSuccess as boolean) ?? false,
          enabledRewardNonCarriage:
            (data.enabledRewardNonCarriage as boolean) ?? false,
          onDemandValidationRequest:
            (data.onDemandValidationRequest as boolean) ?? false,
          onDemandValidationPeriodDays: data.onDemandValidationPeriodDays
            ? Number(data.onDemandValidationPeriodDays)
            : undefined,
          onDemandValidationDays: data.onDemandValidationDays as number[],
          minutesPingGeofenceValidationRequest:
            data.minutesPingGeofenceValidationRequest
              ? Number(data.minutesPingGeofenceValidationRequest)
              : undefined,
          metersGeofenceAccuracyThreshold: data.metersGeofenceAccuracyThreshold
            ? Number(data.metersGeofenceAccuracyThreshold)
            : undefined,
          metersRadiusGeofenceRequestValidation:
            data.metersRadiusGeofenceRequestValidation
              ? Number(data.metersRadiusGeofenceRequestValidation)
              : undefined,
          requestGeofenceValidationsPerDay:
            data.requestGeofenceValidationsPerDay
              ? Number(data.requestGeofenceValidationsPerDay)
              : undefined,
          minimumMinsGeofenceBetweenRequestValidations:
            data.minimumMinsGeofenceBetweenRequestValidations
              ? Number(data.minimumMinsGeofenceBetweenRequestValidations)
              : undefined,
          minutesPingGeofenceAlive: data.minutesPingGeofenceAlive
            ? Number(data.minutesPingGeofenceAlive)
            : undefined,
          stickyAndroidNotificationTitle:
            (data.stickyAndroidNotificationTitle as string) || undefined,
          stickyAndroidNotificationText:
            (data.stickyAndroidNotificationText as string) || undefined,
        },
        enabledRewardUponAccountCreation:
          (data.enabledRewardUponAccountCreation as boolean) ?? false,
        participantUserGuideURL: (data.participantUserGuideURL as any) || [],
        educationalMaterials: (data.educationalMaterials as any) || [],
      },

      // events
      events:
        (data.events as any)?.length > 0 ? (data.events as any) : undefined,
    };

    await updateProgram.mutateAsync(
      {
        id: programId as string,
        data: payload,
      },
      {
        onSuccess: () => {
          showSnackBar(t("messages.update-success"), "success");

          createAuditLog.mutate({
            appType: "WEB_BACK_OFFICE",
            module: MODULE_NAME.PROGRAM,
            option: OPTIONS.PROGRAM_EDIT,
            actionCode: ACTION_CODE.WEB_PROG_EDIT,
            action: "When editing a program",
            detail: `Edited program ${data.name}`,
            transactionDate: new Date(),
            accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
            createdBy: userInfo?._id || "",
            name: `${userInfo?.firstName} ${userInfo?.lastName}`,
          });
          navigate("/programs");
        },
        onError: (error: ErrorResponse) => {
          showSnackBar(t("messages.add-error"), "error");
        },
      },
    );
  };

  return (
    <Box>
      <Breadcrumbs
        items={[
          {
            label: t("page-title"),
            href: "/programs",
            isLink: true,
          },
          {
            label: t("breadcrumb.configuration"),
            href: "/programs/" + programId,
            isLink: true,
          },
        ]}
      />

      <Box
        mt={1}
        mb={4}
        sx={{
          width: "100%",
        }}
        display="flex"
        justifyContent="space-between"
        alignItems={"center"}
      >
        <Typography
          color={theme.palette.textMain.dark2}
          variant="headlineSmall"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          {t("page-create")}

          <LightTooltip title={t("messages.information")} arrow theme={theme}>
            <InfoOutlinedIcon
              sx={{
                cursor: "pointer",
              }}
            />
          </LightTooltip>
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <FormProvider {...methods}>
            <Box sx={{ width: "100%", minHeight: 380 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                }}
              >
                <Tabs
                  value={selectedTab}
                  onChange={handleTabChange}
                  aria-label="invite user tabs"
                >
                  <Tab label={t("tabs.cm-program")} {...a11yProps(0)} />
                  <Tab label={t("tabs.program-ous")} {...a11yProps(1)} />
                  <Tab label={t("tabs.configuration")} {...a11yProps(2)} />
                  <Tab
                    label={t("tabs.geofence", "Geofence & Validation")}
                    {...a11yProps(3)}
                  />
                </Tabs>
              </Box>
              <TabPanel value={selectedTab} index={0}>
                <TabProgramGeneral editMode={true} />
              </TabPanel>
              <TabPanel value={selectedTab} index={1}>
                <TabProgramOUs />
              </TabPanel>
              <TabPanel value={selectedTab} index={2}>
                <TabProgramConfiguration />
              </TabPanel>
              <TabPanel value={selectedTab} index={3}>
                <TabProgramGeofence editMode={true} />
              </TabPanel>
            </Box>
          </FormProvider>
          <Box
            sx={{
              display: "flex",
              gap: "10px",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variantType="cancel"
              onClick={() => navigate("/programs")}
              disabled={false}
              label={t("buttons.cancel")}
            />
            <Button
              variantType="save"
              onClick={async () => {
                const result = await methods.trigger();
                if (!result) {
                  const errors = methods.formState.errors;
                  console.log("Validation errors:", errors);

                  // Find first error and show it
                  const firstErrorKey = Object.keys(errors)[0];
                  const firstError =
                    errors[firstErrorKey as keyof typeof errors];
                  const errorMessage =
                    firstError?.message || "Please check all required fields";

                  showSnackBar(errorMessage, "error");
                  return;
                }

                methods.handleSubmit(onSubmit)();
              }}
              disabled={false}
              isLoading={false}
              label={t("buttons.save")}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EditProgramPage;
